import React, { ReactNode, forwardRef, useState, useEffect, useImperativeHandle } from 'react';
import { unmountComponentAtNode } from 'react-dom';
import Icon from '../Icon';
import * as icons from './icons';
import { useStyles } from './styles';

export type MessageType = keyof typeof icons;

export interface MessageProps {
	root: HTMLDivElement;
	type: MessageType;
	content: ReactNode;
	duration?: number;
	showClose?: boolean;
	onClose?: () => void;
}

const Message = forwardRef<{}, MessageProps>((
	{
		root,
		type,
		content,
		duration,
		showClose,
		onClose,
	},
	ref
) => {
	const [visible, setVisible] = useState(false);
	const [children, update] = useState(content);
	const destroy = () => setVisible(false);
	const onTransitionEnd = () => {
		if (!visible) {
			unmountComponentAtNode(root);

			if (root.parentNode) {
				root.parentNode.removeChild(root);
			}

			if (typeof onClose === 'function') {
				onClose();
			}
		}
	};
	const styles = useStyles([type, visible]);

	useEffect(() => {
		window.setTimeout(() => setVisible(true), 10);

		if (duration) {
			const timer = window.setTimeout(destroy, duration);

			return () => {
				window.clearTimeout(timer);
			};
		}
		return undefined;
	}, []);
	useImperativeHandle(ref, () => ({ update, destroy }));

	return (
		<div
			className={styles.message}
			onTransitionEnd={onTransitionEnd}
		>
			<Icon
				className={styles.icon}
				icon={icons[type]}
			/>
			<div className={styles.content}>
				{children}
			</div>
			{showClose && (
				<div
					className={styles.close}
					onClick={() => setVisible(false)}
				>
					×
				</div>
			)}
		</div>
	);
});

export default Message;
